'use client';

import React from 'react';
import { Coffee, Image, Frame } from 'lucide-react';
import { MerchandiseCategory } from '@/types/merchandise';

interface LocalizedPrice {
  amount: number;
  currency: string;
  symbol: string;
  formatted: string;
  isApproximate: boolean;
}

interface MerchandiseProductCardProps {
  id: string;
  name: string;
  category: MerchandiseCategory;
  description: string;
  previewImageUrl: string;
  pricing: {
    productPrice: number;
    shippingCost: number;
    total: number;
    formatted: {
      productPrice: string;
      shippingCost: string;
      total: string;
    };
    localized?: {
      productPrice: LocalizedPrice;
      shippingCost: LocalizedPrice;
      total: LocalizedPrice;
      currency: string;
      countryCode: string;
    };
  };
  estimatedDelivery: {
    min: number;
    max: number;
  };
  onSelect: (productId: string) => void;
  isSelected?: boolean;
  isLoading?: boolean;
}

const categoryIcons: Record<MerchandiseCategory, typeof Coffee> = {
  mug: Coffee,
  poster: Image,
  canvas: Frame,
};

const categoryLabels: Record<MerchandiseCategory, string> = {
  mug: 'Ceramic Mug',
  poster: 'Wall Poster',
  canvas: 'Canvas Print',
};

export const MerchandiseProductCard: React.FC<MerchandiseProductCardProps> = ({
  id,
  name,
  category,
  description,
  previewImageUrl,
  pricing,
  estimatedDelivery,
  onSelect,
  isSelected = false,
  isLoading = false,
}) => {
  const Icon = categoryIcons[category];

  return (
    <div
      className={`
        relative bg-white rounded-xl overflow-hidden border-2 transition-all duration-200
        ${isSelected
          ? 'border-purple-500 shadow-lg ring-2 ring-purple-200'
          : 'border-gray-100 hover:border-purple-200 hover:shadow-md'
        }
        ${isLoading ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}
      `}
      onClick={() => !isLoading && onSelect(id)}
    >
      {/* Selected Badge */}
      {isSelected && (
        <div className="absolute top-3 right-3 z-10">
          <div className="bg-purple-500 text-white text-xs px-2 py-1 rounded-full font-medium">
            Selected
          </div>
        </div>
      )}

      {/* Product Image Preview */}
      <div className="relative aspect-square bg-gray-100 overflow-hidden">
        {/* Product mockup with user's image */}
        <div className="absolute inset-0 flex items-center justify-center p-4">
          {category === 'mug' && (
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Mug shape with image */}
              <div className="relative w-3/4 h-3/4">
                <div className="absolute inset-0 bg-white rounded-lg shadow-md flex items-center justify-center overflow-hidden">
                  <img
                    src={previewImageUrl}
                    alt={name}
                    className="w-4/5 h-4/5 object-cover rounded"
                  />
                </div>
                {/* Mug handle */}
                <div className="absolute right-0 top-1/4 w-4 h-1/2 border-r-4 border-t-4 border-b-4 border-gray-300 rounded-r-full transform translate-x-3" />
              </div>
            </div>
          )}
          {category === 'poster' && (
            <div className="relative w-3/4 h-3/4 bg-white shadow-lg rounded overflow-hidden">
              <img
                src={previewImageUrl}
                alt={name}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          {category === 'canvas' && (
            <div className="relative w-3/4 h-3/4">
              {/* Canvas frame effect */}
              <div className="absolute inset-0 bg-gray-200 shadow-lg" style={{ transform: 'perspective(100px) rotateY(1deg)' }}>
                <div className="absolute inset-2 bg-white overflow-hidden">
                  <img
                    src={previewImageUrl}
                    alt={name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Product Details */}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <Icon className="h-4 w-4 text-purple-500" />
          <span className="text-xs text-gray-500 uppercase tracking-wide">
            {categoryLabels[category]}
          </span>
        </div>

        <h3 className="font-semibold text-gray-900 mb-1">{name}</h3>
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{description}</p>

        {/* Pricing - show localized if available */}
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <span className="text-lg font-bold text-purple-600">
              {pricing.localized?.productPrice.formatted || pricing.formatted.productPrice}
              {pricing.localized?.productPrice.isApproximate && (
                <span className="text-xs text-gray-400 ml-1">~</span>
              )}
            </span>
            <span className="text-sm text-gray-500 ml-1">
              + {pricing.localized?.shippingCost.formatted || pricing.formatted.shippingCost} shipping
            </span>
          </div>
        </div>

        {/* Delivery estimate */}
        <p className="text-xs text-gray-500">
          Delivered in {estimatedDelivery.min}-{estimatedDelivery.max} business days
        </p>
      </div>

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent" />
        </div>
      )}
    </div>
  );
};

export default MerchandiseProductCard;
